import React from 'react'
import { Theme, withStyles } from '@material-ui/core/styles'
import Dialog from '@material-ui/core/Dialog'
import MuiDialogContent from '@material-ui/core/DialogContent'
import MuiDialogActions from '@material-ui/core/DialogActions'
import { Grid, makeStyles } from '@material-ui/core'
import { isMobile } from 'react-device-detect'
import close from '../assets/images/colosePopup.svg'

const DialogContent = withStyles((theme: Theme) => ({
  root: {
    padding: 0,
  },
}))(MuiDialogContent)

const DialogActions = withStyles((theme: Theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1),
  },
}))(MuiDialogActions)

interface ChartTooltipModalProps {
  open?: any
  setOpen?: any
  handleClose?: any
  tooltipInfo?: any
}
const useStyles = makeStyles({
  content: {
    background: '#FFFFFF',
    boxSizing: 'border-box',
    borderRadius: 3,
    maxWidth: 820,
    width: '100%',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    padding: '4px 24px 24px 24px',
  },
  title: {
    color: '#181818',
    fontFamily: 'Inter, sans-serif',
    fontSize: 24,
    lineHeight: '28px',
    fontWeight: 600,
  },
  closeBtn: {
    width: 36,
    height: 36,
    borderRadius: 999,
    background: '#F2F2F2',
    boxSizing: 'border-box',
  },
  subTitle: {
    fontFamily: 'Inter, sans-serif',
    fontSize: 24,
    lineHeight: '28px',
    color: '#5E5E5E',
    fontWeight: 400,
  },
  detailContent: {
    marginBottom: 12,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: 12,
    marginBottom: 16,
    borderBottom: '1px solid #DFE4EB',
    '& input': {
      width: 36,
      height: 36,
      borderRadius: 999,
      background: '#F2F2F2',
      boxSizing: 'border-box',
    },
  },
  mobileHeader: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    columnGap: 12,
    alignItems: 'start',
    width: '100%',
  },
  mobileTitle: {
    minWidth: 0,
    paddingRight: 8,
  },
  body: {
    overflowY: 'auto',
    paddingRight: 4,
  },
  typeHead: {
    fontFamily: 'Inter, sans-serif',
    fontSize: 14,
    lineHeight: '22px',
    fontWeight: 700,
    color: '#1B3963',
  },
  pDetail: {
    marginTop: 10,
    lineHeight: '22px',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 500,
    fontSize: 14,
    color: '#475569',
  },
  typeDetail: {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 500,
    fontSize: 14,
    lineHeight: '22px',
    color: '#475569',
    '& b': {
      fontWeight: 700,
      color: '#1B3963',
    },
  },
})

const DriverTooltipModal = ({
  open,
  setOpen,
  handleClose,
  tooltipInfo,
}: ChartTooltipModalProps) => {
  const classes = useStyles()
  return (
    <div>
      <Dialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        maxWidth={false}
        PaperProps={{ style: { maxWidth: 820, width: '100%' } }}
      >
        <DialogContent>
          <div className={classes.content}>
            {isMobile && (
              <div className={classes.header}>
                <div className={classes.mobileHeader}>
                  <div className={classes.mobileTitle}>
                    <span className={classes.title}>{tooltipInfo.title}</span>
                    {tooltipInfo.subTitle && (
                      <span className={classes.subTitle}>
                        {' '}
                        ({tooltipInfo.subTitle})
                      </span>
                    )}
                  </div>
                  <input
                    className={classes.closeBtn}
                    type="image"
                    onClick={() => handleClose()}
                    src={close}
                    alt="closeBtn"
                  />
                </div>
              </div>
            )}

            {!isMobile && (
              <div className={classes.header}>
                <div>
                  <span className={classes.title}>{tooltipInfo.title}</span>
                  {tooltipInfo.subTitle && (
                    <span className={classes.subTitle}>
                      {' '}
                      ({tooltipInfo.subTitle})
                    </span>
                  )}
                </div>
                <input
                  type="image"
                  onClick={() => handleClose()}
                  src={close}
                  alt="closeBtn"
                />
              </div>
            )}
            <div className={classes.body}>
              <div className={classes.detailContent}>
                <div className={classes.pDetail}>
                  <p>
                    This chart allows you to look at the underlying drivers of
                    the credit Stress measure; Asset volatility, market value of
                    leverage and market value of assets. You can switch these on
                    and off using the appropriate toggles, with the ability to
                    overlay them all into one view. The tool tip will show you
                    the data points of each driver, allowing for a quick and
                    easy combined assessment.
                  </p>
                  <p style={{ marginTop: 10 }}>
                    The definition of each of the driving components:
                  </p>
                </div>
              </div>
              <div className={classes.detailContent}>
                <span className={classes.typeHead}>
                  MARKET VALUE OF ASSETS (ASSET VALUE):{' '}
                </span>
                <span className={classes.typeDetail}>
                  The market’s view of the enterprise value of the firm as
                  determined by the firm’s equity value, equity volatility and
                  liability structure. We use a proprietary option theoretic
                  model to compute the market value of assets, since it is not
                  directly observable.
                </span>
              </div>
              <div className={classes.detailContent}>
                <span className={classes.typeHead}>MARKET LEVERAGE: </span>
                <span className={classes.typeDetail}>
                  The market leverage is a ratio indicating how much of the
                  market value of the firm’s assets is financed by debt. It is
                  calculated by dividing the default point (The level at which
                  an enterprise is assumed to default on its debt obligations)
                  by the market value of assets. If everything else is equal,
                  the higher the firm’s market leverage, the higher the firm’s
                  credit stress measure credit measure.
                </span>
              </div>
              <div className={classes.detailContent}>
                <span className={classes.typeHead}>ASSET VOLATILITY: </span>
                <span className={classes.typeDetail}>
                  Asset volatility is a measure of the business risk of the
                  firm. Technically, it is the standard deviation of the annual
                  change in the market value of the assets, as measured against
                  the firm’s benchmark group, expressed in percentage terms. The
                  higher the asset volatility, the less certain investors are
                  about the market value of the firm, and the more likely the
                  firm’s value will fall below its default point.
                </span>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default React.memo(DriverTooltipModal)
